/**
 * Which questions on a questionnaire are still unanswered.
 *
 * These rules mirror trm-backend/src/questionnaire/validation/completeness.ts;
 * keep the two in step. A per-grade answer counts only when the grade holds a
 * non-blank value, so the empty scaffold the form seeds ({"A1": [], ...}) is
 * not an answer.
 *
 * The rules follow what each question renders:
 *
 *   id  1  LTI Eligibility          grade -> "all" | "some" | "none"
 *   id  4  LTI Plan Types           eligible grade -> [plan, ...]
 *   id  5  LTI Perf. Conditions     plan -> [condition, ...]      (NOT per grade)
 *   id  7  LTI Vesting Methodology  eligible grade -> [...]
 *   id  8  LTI Vesting Period       eligible grade -> plan -> [...]
 *   id  9  LTI Settlement           eligible grade -> plan -> value
 *   id 10  STI Eligibility          grade -> "all" | "some" | "none"
 *   id 14  STI Perf. measurement    eligible grade -> [...]
 *   id 15  STI Perf. conditions     {value: [...], other: ""}
 *   id 16  STI Perf. Modifiers      {value: [...], other: ""}
 *   id 17  LTI Annual Awards        {base, <base container> -> grade -> {...}}
 *   id 18  STI Annual Awards        {base, <base container> -> grade -> {...}}
 *   id 19  STI Plan Types           eligible grade -> [...]
 *
 * Eligibility cascades: LTI questions take their grades from Q1 and STI
 * questions from Q10, counting only grades marked "all" or "some". A question
 * with no eligible grades has nothing to answer and is treated as satisfied,
 * that is the cascade the form itself implements, and demanding an answer
 * there would make submission impossible.
 */

/** Grades whose eligibility answer opts them in. */
const ELIGIBLE = ['all', 'some'];

/** LTI questions take their eligible grades from Q1, STI questions from Q10. */
const LTI_ELIGIBILITY_ID = 1;
const STI_ELIGIBILITY_ID = 10;
const LTI_DEPENDENTS = [4, 5, 7, 8, 9, 17];
const STI_DEPENDENTS = [14, 18, 19];

/**
 * Unwrap an answer down to the value itself.
 *
 * The envelope nests to different depths depending on where the answer came
 * from: the form holds {value}, storage holds {type, value, metadata}, and the
 * API's raw-create transformer emits {value: {value, type}, metadata}.
 * Unwrapping a fixed number of times reads the envelope on one path and the
 * value on another, so unwrap until there is no envelope left.
 *
 * Multi-selects are {value, other} and are the value, not an envelope, so the
 * presence of `other` stops the unwrapping.
 */
export const actualValue = (answer) => {
  let v = answer;
  while (
    v !== null &&
    v !== undefined &&
    typeof v === 'object' &&
    !Array.isArray(v) &&
    Object.prototype.hasOwnProperty.call(v, 'value') &&
    !Object.prototype.hasOwnProperty.call(v, 'other')
  ) {
    v = v.value;
  }
  return v;
};

/**
 * True when every leaf under a value is blank.
 * The form seeds per-grade scaffolds like {"A1": [], "A2": []}, so a present
 * key proves nothing; only a non-blank leaf does.
 */
export const isBlank = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (typeof value === 'number' || typeof value === 'boolean') return false;
  if (Array.isArray(value)) return value.length === 0 || value.every(isBlank);
  if (typeof value === 'object') {
    const keys = Object.keys(value);
    return keys.length === 0 || keys.every((k) => isBlank(value[k]));
  }
  return false;
};

/** Grades marked "all" or "some" on the given eligibility question. */
export const eligibleGrades = (answers, eligibilityId) => {
  const value = actualValue(answers[eligibilityId]);
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
  return Object.keys(value).filter((grade) => {
    const v = value[grade];
    return typeof v === 'string' && ELIGIBLE.includes(v.trim().toLowerCase());
  });
};

/**
 * Where an annual-awards answer keeps its per-grade figures, by chosen basis.
 * Both percentage bases share one container, and question 17 has written the
 * monetary one under two names over its life, so both are accepted.
 */
const AWARD_CONTAINERS = {
  percentage_base: ['percentages'],
  percentage_tgp: ['percentages'],
  monetary_value: ['monetary_value', 'monetary'],
  other: ['other'],
};

/** Which eligibility question governs a given question, if any. */
const governingEligibility = (id) => {
  if (LTI_DEPENDENTS.includes(id)) return LTI_ELIGIBILITY_ID;
  if (STI_DEPENDENTS.includes(id)) return STI_ELIGIBILITY_ID;
  return null;
};

/** Every plan type selected for any eligible grade in LTI Plan Types (Q4). */
const selectedPlans = (answers) => {
  const value = actualValue(answers[4]);
  if (!value || typeof value !== 'object') return [];
  const plans = new Set();
  eligibleGrades(answers, LTI_ELIGIBILITY_ID).forEach((grade) => {
    const forGrade = value[grade];
    if (Array.isArray(forGrade)) forGrade.filter((p) => !isBlank(p)).forEach((p) => plans.add(p));
  });
  return [...plans];
};

/**
 * Plans the respondent said carry performance conditions.
 *
 * LTI Plan Types keeps a per-plan flag under `value_b`, and the conditions
 * question renders a row only for plans set to performance_conditions_apply.
 */
const plansWithConditions = (answers) => {
  const value = actualValue(answers[4]);
  const flags = value && typeof value === 'object' ? value.value_b : undefined;
  if (!flags || typeof flags !== 'object') return [];
  return Object.keys(flags).filter((plan) => flags[plan] === 'performance_conditions_apply');
};

/** Grades in `grades` whose entry in `value` is blank. */
const gradesMissing = (value, grades) =>
  grades.filter((grade) => !value || typeof value !== 'object' || isBlank(value[grade]));

/**
 * Per question, the grades (or plans) still needing an answer.
 * Returns [] when the question is satisfied or has nothing to answer.
 */
const missingFor = (id, answers) => {
  const value = actualValue(answers[id]);

  // Multi-selects: the inner list carries the answer, `other` is free text.
  if (id === 15 || id === 16) {
    const list = value && typeof value === 'object' && !Array.isArray(value) ? value.value : value;
    return isBlank(list) ? ['response'] : [];
  }

  // Eligibility questions themselves: at least one grade must be set. The grade
  // list comes from the grading system rather than the answer, so a per-grade
  // demand here cannot be checked consistently on both sides of the wire.
  if (id === LTI_ELIGIBILITY_ID || id === STI_ELIGIBILITY_ID) {
    return isBlank(value) ? ['eligibility'] : [];
  }

  // Performance conditions are keyed by plan type, not grade, and only the
  // plans flagged as having conditions get a row. Q4 carries that flag in
  // value_b; a plan marked no_performance_conditions is never asked about.
  if (id === 5) {
    const plans = plansWithConditions(answers);
    if (!plans.length) return [];
    return plans.filter((plan) => !value || isBlank(value[plan]));
  }

  const eligibilityId = governingEligibility(id);
  const grades = eligibilityId ? eligibleGrades(answers, eligibilityId) : [];
  // Nothing eligible means nothing to answer; the form renders an empty table.
  if (!grades.length) return [];

  // Annual awards: a base must be chosen, then filled per grade under the
  // container that base selects. The container is not named after the base
  // (both percentage bases write to `percentages`) and question 17 writes the
  // monetary one under two different names, so try each.
  if (id === 17 || id === 18) {
    if (!value || typeof value !== 'object' || isBlank(value.base)) return ['basis'];
    const names = AWARD_CONTAINERS[value.base];
    if (!names) return [];  // an unrecognised basis: nothing further to assert
    const container = names.map((n) => value[n]).find((c) => c !== undefined);
    if (container === undefined) return grades;  // basis chosen, nothing filled in
    // A basis the form renders as a single field rather than a grade table.
    if (typeof container !== 'object' || Array.isArray(container)) {
      return isBlank(container) ? ['figures'] : [];
    }
    return gradesMissing(container, grades);
  }

  // Grade x plan matrices: every eligible grade needs a value for each plan.
  if (id === 8 || id === 9) {
    const plans = selectedPlans(answers);
    if (!plans.length) return [];
    return grades.filter((grade) => {
      const forGrade = value && typeof value === 'object' ? value[grade] : undefined;
      return plans.some((plan) => !forGrade || isBlank(forGrade[plan]));
    });
  }

  // Plain per-grade questions.
  return gradesMissing(value, grades);
};

/**
 * Answers keyed by question id.
 *
 * The form holds a map, but /questionnaires/:id/answers returns an array of
 * answer rows, so an array is re-keyed by questionId rather than indexed by
 * position.
 */
export const byQuestionId = (answers) => {
  if (!answers) return {};
  if (!Array.isArray(answers)) return answers;
  return answers.reduce((acc, a) => {
    if (a && a.questionId !== undefined && a.questionId !== null) {
      acc[String(a.questionId)] = a;
    }
    return acc;
  }, {});
};

/**
 * Questions that still need an answer before the questionnaire is complete.
 *
 * @param answers   answers keyed by question id, or the array the API returns
 * @param questions question metadata; only `display !== false` entries count
 * @returns [{ id, number, title, missing }] ordered by the number the customer sees
 */
export const findIncompleteQuestions = (answers = {}, questions = []) => {
  const byId = byQuestionId(answers);
  return questions
    .filter((q) => q.display !== false)
    .map((q) => ({ ...q, missing: missingFor(q.id, byId) }))
    .filter((q) => q.missing.length > 0)
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((q) => ({ id: q.id, number: q.displayOrder, title: q.title, missing: q.missing }));
};

/** How many of the shown questions are answered, for progress reporting. */
export const completionCount = (answers = {}, questions = []) => {
  const shown = questions.filter((q) => q.display !== false);
  const incomplete = findIncompleteQuestions(answers, questions).length;
  return { completed: shown.length - incomplete, total: shown.length };
};
